class CountdownTimer extends window.HTMLElement {
  constructor() {
    super();

    // Initialize intervals
    this.interval = null;
    this.intervalUntilMidnight = null;
    this.nowInterval = null;
    this.now = null;

    // Initialize countdown ui elements
    this.daysSpan = this.querySelector('[data-days]');
    this.hoursSpan = this.querySelector('[data-hours]');
    this.minutesSpan = this.querySelector('[data-minutes]');
    this.secondsSpan = this.querySelector('[data-seconds]');
  }

  get targetDate() {
    // Get the target date and time from the element attributes.
    const date = this.getAttribute('data-date');
    const time = this.getAttribute('data-end-time') || '00:00';
    const timezone = this.getAttribute('data-store-timezone') || '';

    // Create date using a more consistent format
    try {
      // Try to use the date, time, and timezone in a format all browsers support
      const dateString = `${date}T${time}:00${timezone}`;
      return new Date(dateString);
    } catch (e) {
      console.error('Error parsing target date:', e);
      return new Date(); // Fallback to current date
    }
  }

  get storeTime() {
    // Get the store date and time from the data-store-time attributes.
    const storeTimeStr = this.getAttribute('data-store-time');
    try {
      return new Date(storeTimeStr);
    } catch (e) {
      console.error('Error parsing store time:', e);
      return new Date(); // Fallback to current date
    }
  }

  displayCountdownValues(days, hours, minutes, seconds) {
    // Update the countdown values using style property as per your HTML structure
    this.daysSpan.style.setProperty('--value', days);
    this.hoursSpan.style.setProperty('--value', hours);
    this.minutesSpan.style.setProperty('--value', minutes);
    this.secondsSpan.style.setProperty('--value', seconds);
  }

  calculateTimeDifference(timestamp) {
    // calculate days, hours, minutes, and seconds.
    const days = Math.floor(timestamp / (1000 * 60 * 60 * 24));
    const hours = Math.floor((timestamp % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((timestamp % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((timestamp % (1000 * 60)) / 1000);

    return { days, hours, minutes, seconds };
  }

  clearAllIntervals() {
    // clear countdown interval until target date
    clearInterval(this.interval);

    // clear countdown interval until midnight
    clearInterval(this.intervalUntilMidnight);

    // clear interval for updating store time now
    clearInterval(this.nowInterval);
  }

  displayCountdownTimer(time) {
    if (time <= 0) {
      // reset countdown
      this.displayCountdownValues(0, 0, 0, 0);

      this.clearAllIntervals();
    } else {
      // Get the calculated time difference
      const { days, hours, minutes, seconds } = this.calculateTimeDifference(time);

      // Display the result in the countdown ui elements.
      this.displayCountdownValues(days, hours, minutes, seconds);
    }
  }

  countdownUntilMidnight() {
    // Get the current date and time
    const currentDate = new Date();

    // Calculate the time until midnight
    const midnight = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      currentDate.getDate() + 1, // Next day
      0, // Midnight hour
      0, // Midnight minute
      0, // Midnight second
    );

    // Calculate the difference between now and midnight
    const difference = midnight - currentDate;

    // Display the countdown timer
    this.displayCountdownTimer(difference);
  }

  updateCountdown() {
    // Find the difference between now and the target date and time.
    const endDate = this.targetDate;
    const currentTime = new Date();
    const difference = endDate - currentTime;

    // Display the countdown timer
    this.displayCountdownTimer(difference);
  }

  connectedCallback() {
    // Initialize with the current time
    this.now = new Date();

    // Function to update 'now' every second
    const updateNow = () => {
      this.now = new Date();
    };

    // Update 'now' every second - store the interval ID properly
    this.nowInterval = setInterval(updateNow, 1000);

    if (!isNaN(this.targetDate.getTime())) {
      // check if target date is valid and update countdown every second
      this.interval = setInterval(() => this.updateCountdown(), 1000);
    } else {
      // update countdown every second until midnight (if target date is invalid)
      this.intervalUntilMidnight = setInterval(() => this.countdownUntilMidnight(), 1000);
    }
  }

  disconnectedCallback() {
    this.clearAllIntervals();
  }
}

// Define the custom element.
window.customElements.define('countdown-timer', CountdownTimer);
